import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";

import clsx from "clsx";

import { makeStyles, Paper, useMediaQuery } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";

import ContactDrawer from "../ContactDrawer";
import MessageInput from "../MessageInput/";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import TicketActionButtons from "../TicketActionButtonsCustom";
import MessagesList from "../MessagesList";
import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { ForwardMessageProvider } from "../../context/ForwarMessage/ForwardMessageContext";

import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TagsContainer } from "../TagsContainer";
import { isNil } from 'lodash';
import { EditMessageProvider } from "../../context/EditingMessage/EditingMessageContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import { getTokens } from "../../styles/tokens";

// Mantém a coluna de detalhes presente em notebooks sem esmagar a conversa.
const drawerWidth = 312;

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
  // Envolve o TicketInfo no header: sem minWidth 0 o div não encolhe (min-width:auto) e empurra
  // os botões de ação para fora do painel — o "Resolver" sumia em telas menores.
  headerInfo: {
    display: "flex",
    flex: "1 1 auto",
    minWidth: 0,
    // no celular o nome fica na linha da seta e encolhe; só as ações descem quando não cabem
    [theme.breakpoints.down("sm")]: {
      flex: "1 1 180px",
    },
  },
  headerTags: {
    display: "flex",
    alignItems: "center",
    flex: "0 1 190px",
    minWidth: 34,
    maxWidth: 190,
    marginLeft: 4,
    // Com três colunas, as tags continuam acessíveis pelo painel do contato.
    // Ocultar este atalho nesse intervalo preserva o nome e as ações do ticket.
    "@media (min-width: 1280px) and (max-width: 1499px)": {
      display: "none",
    },
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
    backgroundColor: t.page,
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    minWidth: 0,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "0",
    borderTop: 0,
    borderBottom: 0,
    borderRight: `1px solid ${t.line}`,
    backgroundColor: t.paper,
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    [theme.breakpoints.down('md')]: {
      marginRight: 0,
    },
  },

  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
    [theme.breakpoints.down('md')]: {
      marginRight: 0,
    },
  },
  tagsBar: {
    flex: "none",
    minHeight: 0,
    border: 0,
    borderBottom: `1px solid ${t.line}`,
    borderRadius: 0,
    backgroundColor: t.paper,
    boxShadow: "none",
  },
  };
});

const Ticket = () => {
  const { ticketId } = useParams();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const canPinDetails = useMediaQuery("(min-width: 1280px)");
  const classes = useStyles();

  const { user, socket } = useContext(AuthContext);
  const { setTabOpen } = useContext(TicketsContext);


  // Painel de dados do contato: só abre sozinho se o atendente deixou fixado
  // (preferência guardada no navegador). Antes abria em toda conversa em telas ≥1280px.
  const readPinnedPreference = () => {
    try {
      return localStorage.getItem("contactDrawerPinned") === "true";
    } catch (e) {
      return false;
    }
  };
  const [drawerOpen, setDrawerOpen] = useState(
    () => canPinDetails && readPinnedPreference()
  );
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const [dragDropFiles, setDragDropFiles] = useState([]);
  // Foto do ticket no instante em que a conversa foi aberta: as não lidas (o servidor zera logo
  // em seguida) e o id numérico, que é a chave das bolhas "enviando".
  const [openedTicket, setOpenedTicket] = useState({ uuid: null, id: null, unread: 0 });
  const { companyId } = user;

  // "digitando…/gravando áudio…/online" do contato (WhatsApp não oficial)
  const [presence, setPresence] = useState(null);
  useEffect(() => {
    setPresence(null);
    if (!ticket?.id || !["whatsapp", "webchat"].includes(ticket.channel) || !user.companyId) return undefined;

    let clearTimer = null;
    let renew = null;
    // o webchat avisa a presença sozinho, sem assinatura
    if (ticket.channel === "whatsapp") {
      api.post(`/tickets/${ticket.id}/presence/subscribe`).catch(() => {});
      // a assinatura no servidor vale 30 min; renova enquanto a conversa estiver aberta
      renew = setInterval(() => {
        api.post(`/tickets/${ticket.id}/presence/subscribe`).catch(() => {});
      }, 20 * 60 * 1000);
    }

    const onPresence = (data) => {
      if (data?.ticketId !== ticket.id) return;
      // em grupo, "online" de um participante não diz nada sobre a conversa: só digitando/gravando
      if (ticket.isGroup && data.state !== "composing" && data.state !== "recording") return;
      clearTimeout(clearTimer);
      setPresence(data.state === "offline" ? null : data.state);
      // o WhatsApp nem sempre avisa que a pessoa parou: o "digitando…" expira sozinho
      if (data.state === "composing" || data.state === "recording") {
        clearTimer = setTimeout(() => setPresence(ticket.isGroup ? null : "online"), 15000);
      }
    };
    socket.on(`company-${user.companyId}-presence`, onPresence);

    return () => {
      clearInterval(renew);
      clearTimeout(clearTimer);
      socket.off(`company-${user.companyId}-presence`, onPresence);
    };
  }, [ticket?.id, ticket?.channel, user.companyId, socket]);

  useEffect(() => {
    setDrawerOpen(canPinDetails && readPinnedPreference());
  }, [canPinDetails, ticketId]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchTicket = async () => {
        try {

          if (!isNil(ticketId) && ticketId !== "undefined") {

            const { data } = await api.get("/tickets/u/" + ticketId);

            setOpenedTicket({ uuid: ticketId, id: data.id, unread: Number(data.unreadMessages) || 0 });
            setContact(data.contact);
            // setWhatsapp(data.whatsapp);
            // setQueueId(data.queueId);
            setTicket(data);
            if (["pending", "open", "group"].includes(data.status)) {
              setTabOpen(data.status);
            }
            setLoading(false);
          }
        } catch (err) {
          history.push("/tickets");   // correção para evitar tela branca uuid não encontrado Feito por Altemir 16/08/2023
          setLoading(false);
          toastError(err);
        }
      };
      fetchTicket();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [ticketId, user, history]);

  useEffect(() => {
    if (!ticket && !ticket.id && ticket.uuid !== ticketId && ticketId === "undefined") {
      return;
    }

    if (user.companyId) {
      //    const socket = socketManager.GetSocket();

      const onConnectTicket = () => {
        socket.emit("joinChatBox", `${ticket.id}`);
      }

      const onCompanyTicket = (data) => {
        if (data.action === "update" && data.ticket.id === ticket?.id) {
          setTicket(data.ticket);
        }

        if (data.action === "delete" && data.ticketId === ticket?.id) {
          history.push("/tickets");
        }
      };

      const onCompanyContactTicket = (data) => {
        if (data.action === "update") {
          // if (isMounted) {
          setContact((prevState) => {
            if (prevState.id === data.contact?.id) {
              return { ...prevState, ...data.contact };
            }
            return prevState;
          });
          // }
        }
      };

      socket.on("connect", onConnectTicket)
      socket.on(`company-${companyId}-ticket`, onCompanyTicket);
      socket.on(`company-${companyId}-contact`, onCompanyContactTicket);

      return () => {

        socket.emit("joinChatBoxLeave", `${ticket.id}`);
        socket.off("connect", onConnectTicket);
        socket.off(`company-${companyId}-ticket`, onCompanyTicket);
        socket.off(`company-${companyId}-contact`, onCompanyContactTicket);
      };
    }
  }, [ticketId, ticket, history]);

  const persistPinnedPreference = (value) => {
    try {
      localStorage.setItem("contactDrawerPinned", value ? "true" : "false");
    } catch (e) {
      /* navegador sem storage: segue só em memória */
    }
  };

  const handleDrawerOpen = useCallback(() => {
    setDrawerOpen(true);
    persistPinnedPreference(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    persistPinnedPreference(false);
  }, []);

  const handleQuickMessageSelect = (quickMessage) => {
    try {
      if (quickMessage.message) {
        // Disparar evento que o MessageInput vai escutar
        const event = new CustomEvent('insertQuickMessage', {
          detail: { message: quickMessage.message }
        });
        window.dispatchEvent(event);
        
      }
      
      if (quickMessage.mediaPath) {
        // Tratar mídia se necessário
      }
    } catch (error) {
      console.error("Erro ao inserir resposta rápida:", error);
      toastError("Erro ao inserir resposta rápida");
    }
  };

  const renderMessagesList = () => {
    return (
      <>
        <MessagesList
          isGroup={ticket.isGroup}
          onDrop={setDragDropFiles}
          whatsappId={ticket.whatsappId}
          queueId={ticket.queueId}
          channel={ticket.channel}
          ticketStatus={ticket.status}
          unreadAtOpen={openedTicket.uuid === ticketId ? openedTicket.unread : null}
          ticketNumericId={openedTicket.uuid === ticketId ? openedTicket.id : null}
          groupContactId={ticket.isGroup && openedTicket.uuid === ticketId ? contact?.id : null}
        >
        </MessagesList>
        <MessageInput
          ticketId={ticket.id}
          ticketStatus={ticket.status}
          ticketChannel={ticket.channel}
          droppedFiles={dragDropFiles}
          contactId={contact.id}
          whatsappId={ticket.whatsappId}
          isGroup={ticket.isGroup}
        />
      </>
    );
  };


  return (
    <div className={classes.root} id="drawer-container">
      <Paper
        variant="outlined"
        elevation={0}
        className={clsx(classes.mainWrapper, {
          [classes.mainWrapperShift]: drawerOpen && !isMobile,
        })}
      >
        {/* <div id="TicketHeader"> */}
        <TicketHeader loading={loading}>
          {ticket.contact !== undefined && (
            <div id="TicketHeader" className={classes.headerInfo}>
              <TicketInfo
                contact={contact}
                ticket={ticket}
                presence={presence}
                onClick={handleDrawerOpen}
              />
            </div>
          )}
          {contact?.id && !drawerOpen && (
            <div className={classes.headerTags}>
              <TagsContainer contact={contact} />
            </div>
          )}
        <TicketActionButtons
          ticket={ticket}
          contact={contact}
          onQuickMessageSelect={handleQuickMessageSelect}
        />
        </TicketHeader>
        {/* </div> */}
        <ReplyMessageProvider>
          <ForwardMessageProvider>
            <EditMessageProvider>
              {renderMessagesList()}
            </EditMessageProvider>
          </ForwardMessageProvider>
        </ReplyMessageProvider>
      </Paper>

      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        contact={contact}
        loading={loading}
        ticket={ticket}
      />

    </div>
  );
};

export default Ticket;
